import { insights, isApiError } from '@repletoryreach/sdk-api';
import { addDays, localDateOf } from '../../bookings/lib/time';
import type { Translate } from '../../../i18n/I18nProvider';

// Chat insights in the console (owner, 29 October 2026). The report page filters its chats in the browser, so every
// figure below is worked out here from the rows, the same way whatever the filter: no filter shows the whole period.

export type Row = insights.InsightRow;

export const INSIGHTS_KEY = 'insights';
export const CHANNELS = ['whatsapp', 'web', 'messenger', 'instagram', 'email', 'sms'] as const;
export const OUTCOMES: insights.InsightOutcome[] = ['resolved_by_bot', 'resolved_by_team', 'waiting_for_team', 'abandoned', 'info_only', 'spam'];
export const SENTIMENTS: insights.InsightSentiment[] = ['positive', 'neutral', 'negative'];
export const STAGES: insights.InsightStage[] = ['gather', 'read', 'organise', 'write'];
export const PRESETS = [7, 14, 30, 60, 90] as const;
export const MAX_DAYS = 92;
export const MAX_FOCUS = 5;
export const FOCUS_CHARS = 200;

export const isRunning = (r?: { status: insights.InsightStatus } | null) => r?.status === 'queued' || r?.status === 'running';

/** The days from `from` to `to`, both included (YYYY-MM-DD). */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length <= 400; d = addDays(d, 1)) out.push(d);
  return out;
}

/** What is wrong with a period, as an i18n key; null when it is fine. */
export function periodProblem(from: string, to: string, today: string): string | null {
  if (!from || !to) return 'insights.new.period.missing';
  if (from > to) return 'insights.new.period.order';
  if (to > today) return 'insights.new.period.future';
  if (daysBetween(from, to).length > MAX_DAYS) return 'insights.new.period.long';
  return null;
}

/** The business's own questions, one per line: blank lines dropped, at most five. */
export function parseFocus(text: string): string[] {
  return text.split('\n').map((l) => l.trim()).filter(Boolean);
}

export function focusProblem(lines: string[]): string | null {
  if (lines.length > MAX_FOCUS) return 'insights.new.focus.tooMany';
  if (lines.some((l) => l.length > FOCUS_CHARS)) return 'insights.new.focus.tooLong';
  return null;
}

// ── Filters ────────────────────────────────────────────────────────────────

export interface Filters {
  from: string;
  to: string;
  channel: string;
  topic: string;
  customerType: string;
  outcome: string;
  sentiment: string;
  /** The chats a finding is based on ("See 12 chats"); empty means no such filter. */
  refs: string[];
}

export const emptyFilters = (from: string, to: string): Filters => ({ from, to, channel: '', topic: '', customerType: '', outcome: '', sentiment: '', refs: [] });

/** True when anything narrows the report: then figures are of the chats shown, with no change on the period before. */
export function isFiltered(f: Filters, report: { from: string; to: string }): boolean {
  return f.from !== report.from || f.to !== report.to || !!f.channel || !!f.topic || !!f.customerType || !!f.outcome || !!f.sentiment || f.refs.length > 0;
}

export const localDay = (iso: string, tz: string): string => localDateOf(iso, tz);

export function applyFilters(rows: Row[], f: Filters, tz: string): Row[] {
  const refs = f.refs.length ? new Set(f.refs) : null;
  return rows.filter((r) => {
    if (refs && !refs.has(r.ref)) return false;
    const day = localDay(r.at, tz);
    if (day < f.from || day > f.to) return false;
    if (f.channel && r.channel !== f.channel) return false;
    if (f.topic && !r.topics.includes(f.topic)) return false;
    if (f.customerType && r.customer_type !== f.customerType) return false;
    if (f.outcome && r.outcome !== f.outcome) return false;
    if (f.sentiment && r.sentiment !== f.sentiment) return false;
    return true;
  });
}

// ── Figures ────────────────────────────────────────────────────────────────

export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export interface Kpis {
  chats: number;
  people: number;
  newPeople: number;
  read: number;
  /** Of the chats read (spam left out), the share the chatbot resolved, 0 to 100. */
  answeredByBotPct: number | null;
  handedToTeam: number;
  unanswered: number;
  firstReplyMedian: number | null;
  buyingStrong: number;
  buyingSome: number;
}

export function kpis(rows: Row[]): Kpis {
  const people = new Set(rows.map((r) => r.contact_id ?? r.conversation_id));
  const read = rows.filter((r) => r.read && r.outcome !== 'spam');
  const byBot = read.filter((r) => r.outcome === 'resolved_by_bot').length;
  return {
    chats: rows.length,
    people: people.size,
    newPeople: rows.filter((r) => !r.returning).length,
    read: read.length,
    answeredByBotPct: read.length ? Math.round((byBot / read.length) * 100) : null,
    handedToTeam: rows.filter((r) => r.taken_over).length,
    unanswered: rows.filter((r) => r.unanswered).length,
    firstReplyMedian: median(rows.map((r) => r.first_reply_minutes).filter((m): m is number => m != null)),
    buyingStrong: rows.filter((r) => r.buying_signal === 'strong').length,
    buyingSome: rows.filter((r) => r.buying_signal === 'some').length
  };
}

/** The change from the period before, in percent; null when there is nothing to compare with. */
export function change(now: number, before: number | null | undefined): number | null {
  if (before == null || before === 0) return null;
  return Math.round(((now - before) / before) * 1000) / 10;
}

export function perDay(rows: Row[], from: string, to: string, tz: string): { date: string; chats: number; new_people: number }[] {
  const days = new Map(daysBetween(from, to).map((d) => [d, { date: d, chats: 0, new_people: 0 }]));
  for (const r of rows) {
    const d = days.get(localDay(r.at, tz));
    if (!d) continue;
    d.chats++;
    if (!r.returning) d.new_people++;
  }
  return [...days.values()];
}

const WEEKDAY: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** Chats started by weekday (0 = Sunday) and hour, in the business's time zone. */
export function heat(rows: Row[], tz: string): number[][] {
  const grid = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
  const fmt = new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', hour: '2-digit', hourCycle: 'h23' });
  for (const r of rows) {
    const parts = fmt.formatToParts(new Date(r.at));
    const wd = WEEKDAY[parts.find((p) => p.type === 'weekday')?.value ?? ''];
    const hour = Number(parts.find((p) => p.type === 'hour')?.value);
    if (wd !== undefined && hour >= 0 && hour < 24) grid[wd][hour]++;
  }
  return grid;
}

export function countBy<T extends string>(values: (T | null | undefined)[]): { name: T; count: number }[] {
  const m = new Map<T, number>();
  for (const v of values) if (v) m.set(v, (m.get(v) ?? 0) + 1);
  return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

export interface TopicStat {
  name: string;
  chats: number;
  share: number;
  /** Of the topic's chats, those whose questions were all answered. */
  answered: number;
  handedOver: number;
  trend: number[];
  refs: string[];
}

/** Every question in the chat answered, by the chatbot or the team (a chat with no question counts as answered). */
export const fullyAnswered = (r: Row) => r.questions.every((q) => q.answered === 'yes');

export function topicStats(rows: Row[], names: string[], from: string, to: string, tz: string): TopicStat[] {
  const read = rows.filter((r) => r.read);
  const days = daysBetween(from, to);
  const all = [...new Set([...names, ...read.flatMap((r) => r.topics)])];
  return all.map((name) => {
    const hit = read.filter((r) => r.topics.includes(name));
    const byDay = new Map(days.map((d) => [d, 0]));
    for (const r of hit) {
      const d = localDay(r.at, tz);
      if (byDay.has(d)) byDay.set(d, (byDay.get(d) ?? 0) + 1);
    }
    return {
      name,
      chats: hit.length,
      share: read.length ? Math.round((hit.length / read.length) * 100) : 0,
      answered: hit.filter(fullyAnswered).length,
      handedOver: hit.filter((r) => r.taken_over).length,
      trend: [...byDay.values()],
      refs: hit.map((r) => r.ref)
    };
  }).filter((s) => s.chats > 0).sort((a, b) => b.chats - a.chats || a.name.localeCompare(b.name));
}

/** Topic by customer type: how many chats of each kind of customer were about each topic. */
export function topicByType(rows: Row[], topics: string[]): { types: string[]; cells: Record<string, Record<string, number>> } {
  const read = rows.filter((r) => r.read);
  const types = countBy(read.map((r) => r.customer_type)).map((x) => x.name);
  const cells: Record<string, Record<string, number>> = {};
  for (const topic of topics) {
    cells[topic] = {};
    for (const type of types) cells[topic][type] = read.filter((r) => r.customer_type === type && r.topics.includes(topic)).length;
  }
  return { types, cells };
}

export function answerSplit(rows: Row[]): { yes: number; partly: number; no: number } {
  const out = { yes: 0, partly: 0, no: 0 };
  for (const r of rows) for (const q of r.questions) out[q.answered]++;
  return out;
}

export interface ThemeStat {
  theme: string;
  asked: number;
  chats: number;
  examples: string[];
  refs: string[];
}

/** Questions not fully answered, by theme: how often, in how many chats, with a few of the customers' own words. */
export function unansweredThemes(rows: Row[]): ThemeStat[] {
  const m = new Map<string, ThemeStat>();
  for (const r of rows) {
    for (const q of r.questions) {
      if (q.answered === 'yes' || !q.theme) continue;
      const s = m.get(q.theme) ?? { theme: q.theme, asked: 0, chats: 0, examples: [], refs: [] };
      s.asked++;
      if (!s.refs.includes(r.ref)) { s.refs.push(r.ref); s.chats++; }
      if (s.examples.length < 3 && !s.examples.includes(q.text)) s.examples.push(q.text);
      m.set(q.theme, s);
    }
  }
  return [...m.values()].sort((a, b) => b.asked - a.asked || a.theme.localeCompare(b.theme));
}

export interface StopStat {
  name: string;
  count: number;
  examples: { ref: string; after: string }[];
  refs: string[];
}

export function stopStats(rows: Row[]): StopStat[] {
  const m = new Map<string, StopStat>();
  for (const r of rows) {
    if (!r.stop_reason) continue;
    const s = m.get(r.stop_reason) ?? { name: r.stop_reason, count: 0, examples: [], refs: [] };
    s.count++;
    s.refs.push(r.ref);
    if (r.stopped_after && s.examples.length < 3) s.examples.push({ ref: r.ref, after: r.stopped_after });
    m.set(r.stop_reason, s);
  }
  return [...m.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

export function productStats(rows: Row[]): { name: string; count: number; refs: string[] }[] {
  const m = new Map<string, string[]>();
  for (const r of rows) for (const p of r.products) m.set(p, [...(m.get(p) ?? []), r.ref]);
  return [...m.entries()].map(([name, refs]) => ({ name, count: refs.length, refs })).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

export const leads = (rows: Row[]) => rows
  .filter((r) => r.buying_signal === 'strong' || r.buying_signal === 'some')
  .sort((a, b) => (a.buying_signal === b.buying_signal ? b.at.localeCompare(a.at) : a.buying_signal === 'strong' ? -1 : 1));

export function focusCounts(rows: Row[], q: number): { yes: number; no: number; unclear: number; refs: string[] } {
  const out = { yes: 0, no: 0, unclear: 0, refs: [] as string[] };
  for (const r of rows) {
    const a = r.focus.find((f) => f.q === q);
    if (!a) continue;
    out[a.answer]++;
    if (a.answer === 'yes') out.refs.push(r.ref);
  }
  return out;
}

// ── Exports ────────────────────────────────────────────────────────────────

/** Rows name the flows that ran in a chat by id; the report's flow figures give each its name. */
export const flowNames = (report: insights.InsightReport): Map<string, string> =>
  new Map((report.metrics?.flows ?? []).map((f) => [f.flow_id, f.name]));

const cell = (v: unknown) => {
  const s = v == null ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** The chats shown, as a CSV a spreadsheet opens with the right characters (a byte-order mark first). */
export function rowsCsv(rows: Row[], t: Translate, tz: string, flowNames: ReadonlyMap<string, string> = new Map()): string {
  const head = ['ref', 'date', 'channel', 'person', 'returning', 'topics', 'products', 'customerType', 'outcome', 'sentiment',
    'stopReason', 'stoppedAfter', 'buying', 'buyingDetail', 'unanswered', 'takenOver', 'firstReply', 'flows', 'summary', 'quote']
    .map((k) => t(`insights.csv.${k}`));
  const yes = t('insights.csv.yes'), no = t('insights.csv.no');
  const lines = rows.map((r) => [
    r.ref, localDay(r.at, tz), r.channel, r.contact_name ?? '', r.returning ? yes : no, r.topics.join('; '), r.products.join('; '),
    r.customer_type ?? '', r.outcome ? t(`insights.outcome.${r.outcome}`) : '', r.sentiment ? t(`insights.sentiment.${r.sentiment}`) : '',
    r.stop_reason ?? '', r.stopped_after ?? '', r.buying_signal && r.buying_signal !== 'none' ? t(`insights.buying.${r.buying_signal}`) : '',
    r.buying_detail ?? '', r.unanswered ? yes : no, r.taken_over ? yes : no, r.first_reply_minutes ?? '',
    r.flows.map((id) => flowNames.get(id) ?? id).join('; '), r.summary ?? '', r.quote ?? ''
  ].map(cell).join(','));
  return '﻿' + [head.map(cell).join(','), ...lines].join('\r\n');
}

// ── Errors ─────────────────────────────────────────────────────────────────

/** What went wrong, in the business's words. */
export function insightErrorText(t: Translate, e: unknown): string {
  if (isApiError(e)) {
    if (e.code === insights.INSUFFICIENT_CREDITS || e.status === 402) return t('insights.error.credits');
    if (e.code === insights.REPORT_ACTIVE) return t('insights.error.active');
    if (e.code === insights.NOTHING_TO_READ) return t('insights.error.nothing');
    if (e.code === insights.REPORT_NOT_DONE) return t('insights.error.notDone');
    if (e.status === 403) return t('insights.error.forbidden');
    if (e.message) return e.message;
  }
  return t('common.unknownError');
}
